import { Component, ViewChild, TemplateRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '../services/notification.service';
import { RoleViewService } from '../services/role-view.service';
import { ReportService } from '../services/report.service';

export interface Activity {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  status: string;
  progress?: number;
}

export interface Project {
  id: string;
  name: string;
  code: string;
  color: string;
  activities: Activity[];
}

export interface WeekHeader {
  weekStart: Date;
  weekEnd: Date;
  mondayDate: Date;
  weekNumber: number;
  monthName: string;
}

export interface ActivityBarPosition {
  activity: Activity;
  startColumn: number;
  spanColumns: number;
  leftPercent: number;
  widthPercent: number;
  isVisible?: boolean;
  rowOffset?: number;
}

export interface MonthGroup {
  monthName: string;
  year: number;
  weeks: WeekHeader[];
}

export interface ProjectActivityRow {
  project: Project;
  activity: Activity;
  isFirstActivityOfProject: boolean;
  activityIndex: number;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatTooltipModule
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css'
})
export class ReportsComponent implements OnInit {
  @ViewChild('projectDialogTemplate') projectDialogTemplate!: TemplateRef<any>;
  @ViewChild('taskDialogTemplate') taskDialogTemplate!: TemplateRef<any>;

  currentDate = new Date(); // Track the actual current date
  viewDate = new Date(); // Track the month being viewed - initialized to current date
  projects: Project[] = [];
  weekHeaders: WeekHeader[] = [];
  monthGroups: MonthGroup[] = [];
  timelineStartDate: Date = new Date();
  timelineEndDate: Date = new Date();
  totalTimelineDays = 0;
  monthsToDisplay = 3;
  selectedProjectIds: Set<string> = new Set();
  monthSeparatorPositions: number[] = [];
  workDayId = '';
  userRole = '';
  userName = '';
  activeProjs: any[] = [];

  // Dialog references
  private projectDialogRef?: MatDialogRef<any>;
  private taskDialogRef?: MatDialogRef<any>;

  // Form expansion states
  isProjectFormCollapsed = false;
  isTaskFormCollapsed = false;
  colorErrorMessage = '';

  // New task form properties
  newTask = {
    projectId: '',
    name: '',
    startDate: null as Date | null,
    endDate: null as Date | null
  };

  // Edit task state
  isEditingTask = false;
  editingTaskId: string | null = null;

  // New project form properties
  newProject = {
    name: '',
    color: '#3b82f6'  // Default blue color
  };

  // Project colors mapping
  projectColors: { [key: string]: string } = {
    'GRE': '#FF9D5C', // Orange for G-RIS RCC
    'CPK': '#E8899C', // Pink/Red for G-CIS PICL
    'HAT': '#F0A3A3', // Light red for Homol Acq TN
    'GCP': '#FFB86C', // Orange for G-CIS configurations
    'GRI': '#87CEEB', // Light blue for G-RIS Input
    'EAT': '#A1D97F', // Green for E/A ABL TN
    'ESA': '#E8899C', // Pink for E/S ABL TN
    'GBC': '#FFE066'  // Gold/Yellow for G-RIS RCC Input
  };

  constructor(private readonly dialog: MatDialog, private readonly reportService: ReportService,
    private readonly roleviewservice: RoleViewService,
    private readonly notificationService: NotificationService,) { }
  ngOnInit(): void {
    this.getUserInfo();
    this.initializeProjects();
    this.activeProjectsHandler();
    this.generateWeekHeaders();
    // Initialize all projects as selected

  }

  getUserInfo() {
    this.workDayId = this.roleviewservice.getWorkdayId() || '';
    this.userName = this.roleviewservice.getUserName();
  }

  activeProjectsHandler(): void {
    this.reportService.fetchActiveProjectHandler().subscribe(res => {
      this.activeProjs = res.payload.map((project: any) => ({
        id: project.project_id,
        name: project.project_name,
        code: project.color_code,
        color: project.color_name,
        isDeleted: project.is_deleted,
      }));
      console.info("Active Projects fetched successfully", this.activeProjs);
    });
  }
  // Dialog Methods
  openProjectDialog(): void {
    this.projectDialogRef = this.dialog.open(this.projectDialogTemplate, {
      width: '450px',
      maxWidth: '90vw',
      panelClass: 'custom-dialog-container',
      disableClose: false
    });
  }

  openTaskDialog(projectId?: string, activity?: Activity): void {
    if (projectId && activity) {
      this.isEditingTask = true;
      this.editingTaskId = activity.id;
      this.newTask = {
        projectId: projectId,
        name: activity.name,
        startDate: new Date(activity.startDate),
        endDate: new Date(activity.endDate)
      };
    } else {
      this.isEditingTask = false;
      this.editingTaskId = null;
      this.newTask = {
        projectId: '',
        name: '',
        startDate: null,
        endDate: null
      };
    }

    this.taskDialogRef = this.dialog.open(this.taskDialogTemplate, {
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'custom-dialog-container',
      disableClose: false
    });
  }

  initializeProjects(): void {
    this.reportService.fetchActivityHandler().subscribe(res => {
      this.projects = res.payload.map((project: any) => ({
        id: project.project_id,
        name: project.project_name,
        code: project.project_color_code,
        color: project.project_color_name,
        activities: project.activities.map((activity: any) => ({
          id: activity.activity_task_id,
          name: activity.task_description,
          startDate: activity.start_date,
          endDate: activity.end_date,
        }))
      }));

      // Ensure activities are ordered chronologically so overlap calculations work reliably
      this.projects.forEach(project => {
        if (project.activities && project.activities.length > 1) {
          project.activities = project.activities.slice().sort((a, b) => {
            const aStart = new Date(a.startDate).getTime();
            const bStart = new Date(b.startDate).getTime();
            if (aStart !== bStart) {
              return aStart - bStart;
            }
            const aEnd = new Date(a.endDate).getTime();
            const bEnd = new Date(b.endDate).getTime();
            return aEnd - bEnd;
          });
        }
      });

      this.projects.forEach(project => {
        this.selectedProjectIds.add(project.id);
      });
    });

    console.info('Projects initialized:', this.projects);
  }

  onEditActivity(projectId: string, activity: Activity): void {
    this.openTaskDialog(projectId, activity);
  }

  onDeleteActivity(projectId: string, activityId: string): void {
    const project = this.projects.find(p => p.id === projectId);
    if (!project) {
      return;
    }
    project.activities = project.activities.filter(a => a.id !== activityId);
    console.info("Activity deleted:", activityId, project.activities);
    const payload = {
      updatedBy: this.userName,
      taskId: String(activityId)
    };
    this.reportService.deleteActivityHandler(payload).subscribe(res => {
      console.info("Activity deleted successfully from backend", res);
      this.notificationService.showSuccess('Activity deleted successfully!');
      this.initializeProjects();
    });
  }

  generateWeekHeaders(): void {
    this.weekHeaders = [];
    const startMonth = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);

    // Get the start and end of the visible 3-month period
    const viewStartDate = this.getMonthStart(startMonth);
    const viewMonday = this.getMonday(viewStartDate);

    // Store visible period for activity positioning
    this.timelineStartDate = new Date(viewMonday);

    // Calculate end date: 3 months from start month
    const endMonth = new Date(startMonth.getFullYear(), startMonth.getMonth() + 3, 1);
    const threeMonthsEnd = this.getMonday(endMonth);

    this.timelineEndDate = new Date(threeMonthsEnd);

    // Calculate total days for reference
    this.totalTimelineDays = Math.ceil((this.timelineEndDate.getTime() - this.timelineStartDate.getTime()) / (24 * 60 * 60 * 1000));

    // Generate week headers
    let currentWeekStart = new Date(viewMonday);
    let weekNumber = 1;
    while (currentWeekStart < threeMonthsEnd) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      this.weekHeaders.push({
        weekStart: currentWeekStart,
        weekEnd: weekEnd,
        mondayDate: new Date(currentWeekStart),
        weekNumber: weekNumber,
        monthName: this.getMonthName(currentWeekStart)
      });

      currentWeekStart = new Date(currentWeekStart);
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      weekNumber++;
    }

    // Group weeks by month
    this.groupWeeksByMonth();

    // Calculate month separator positions
    this.calculateMonthSeparators();
  }

  groupWeeksByMonth(): void {
    this.monthGroups = [];
    const groupMap = new Map<string, WeekHeader[]>();
    const monthYearMap = new Map<string, number>();

    // Group weeks by month and year
    this.weekHeaders.forEach(week => {
      const year = week.mondayDate.getFullYear();
      const monthKey = `${week.monthName}-${year}`;
      if (!groupMap.has(monthKey)) {
        groupMap.set(monthKey, []);
        monthYearMap.set(monthKey, year);
      }
      const weeks = groupMap.get(monthKey);
      if (weeks) {
        weeks.push(week);
      }
    });

    // Convert to array of MonthGroup, maintaining order
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sortedKeys = Array.from(groupMap.keys()).sort((a, b) => {
      const [monthA, yearA] = a.split('-');
      const [monthB, yearB] = b.split('-');
      const yearDiff = Number.parseInt(yearA) - Number.parseInt(yearB); // Ascending order (earlier years first)
      if (yearDiff !== 0) {
        return yearDiff;
      }
      return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
    });

    sortedKeys.forEach(key => {
      const weeks = groupMap.get(key) ?? [];
      const year = monthYearMap.get(key) ?? 0;
      const monthName = key.split('-')[0];
      this.monthGroups.push({
        monthName: monthName,
        year: year,
        weeks: weeks
      });
    });
  }

  previousMonth(): void {
    // Create new date object to avoid mutation issues
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.generateWeekHeaders();
  }

  nextMonth(): void {
    // Create new date object to avoid mutation issues
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.generateWeekHeaders();
  }

  goToToday(): void {
    this.currentDate = new Date();
    this.viewDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
    this.generateWeekHeaders();
  }

  getPreviousMonthLabel(): string {
    const prevMonth = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    return this.formatMonthYear(prevMonth);
  }

  currentMonthLabel(): string {
    return this.formatMonthYear(this.viewDate);
  }

  getNextMonthLabel(): string {
    const nextMonth = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    return this.formatMonthYear(nextMonth);
  }

  private formatMonthYear(date: Date): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  getMonthStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  getMonthName(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[date.getMonth()];
  }

  getVisibleProjects(): Project[] {
    return this.projects
      .filter(project => this.isProjectVisible(project.id))
      .sort((a, b) => {
        const aHasActivities = a.activities && a.activities.length > 0;
        const bHasActivities = b.activities && b.activities.length > 0;

        // Projects with activities come first
        if (aHasActivities && !bHasActivities) {
          return -1;
        }
        if (!aHasActivities && bHasActivities) {
          return 1;
        }
        return 0;
      });
  }

  calculateActivityPosition(activity: Activity): ActivityBarPosition {
    const activityStart = new Date(activity.startDate);
    activityStart.setHours(0, 0, 0, 0);

    const activityEnd = new Date(activity.endDate);
    activityEnd.setHours(23, 59, 59, 999);

    const visibleStart = this.timelineStartDate.getTime();
    const visibleEnd = this.timelineEndDate.getTime();
    const visibleDuration = visibleEnd - visibleStart;

    const actStart = activityStart.getTime();
    const actEnd = activityEnd.getTime();

    // Clamp activity to visible range
    const clampedStart = Math.max(actStart, visibleStart);
    const clampedEnd = Math.min(actEnd, visibleEnd);

    // If activity is completely outside visible range, return minimal values
    if (clampedStart >= clampedEnd) {
      return {
        activity: activity,
        startColumn: 0,
        spanColumns: 0,
        leftPercent: 0,
        widthPercent: 0,
        isVisible: false
      };
    }

    const leftPercent = ((clampedStart - visibleStart) / visibleDuration) * 100;
    const widthPercent = ((clampedEnd - clampedStart) / visibleDuration) * 100;

    const daysFromStart = Math.floor((clampedStart - visibleStart) / (24 * 60 * 60 * 1000));
    const daysDuration = Math.ceil((clampedEnd - clampedStart) / (24 * 60 * 60 * 1000));

    return {
      activity: activity,
      startColumn: daysFromStart,
      spanColumns: daysDuration,
      leftPercent: leftPercent,
      widthPercent: widthPercent,
      isVisible: true
    };
  }

  getActivityDuration(activity: Activity): string {
    const start = new Date(activity.startDate);
    const end = new Date(activity.endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    return `${days}d`;
  }

  getActivityBarStyle(activity: Activity, projectColor: string, activities: Activity[]): any {
    const position = this.calculateActivityPosition(activity);
    const rowOffset = this.calculateRowOffset(activity, activities);
    return {
      'background-color': projectColor,
      'left': position.leftPercent + '%',
      'width': position.widthPercent + '%',
      'top': `${8 + (rowOffset * 44)}px`
    };
  }

  calculateRowOffset(currentActivity: Activity, activities: Activity[]): number {
    const sortedActivities = activities.slice().sort((a, b) => {
      const aPos = this.calculateActivityPosition(a);
      const bPos = this.calculateActivityPosition(b);

      const aStart = aPos.leftPercent;
      const bStart = bPos.leftPercent;
      if (aStart !== bStart) {
        return aStart - bStart;
      }
      const aEnd = aPos.leftPercent + aPos.widthPercent;
      const bEnd = bPos.leftPercent + bPos.widthPercent;
      return aEnd - bEnd;
    });

    interface RowState { end: number; }
    const rows: RowState[] = [];
    let rowForCurrent = 0;

    for (const activity of sortedActivities) {
      const pos = this.calculateActivityPosition(activity);
      if (!pos.isVisible) {
        continue;
      }

      const start = pos.leftPercent;
      const end = pos.leftPercent + pos.widthPercent;

      // Find the first row where this activity does not overlap
      let rowIndex = 0;
      while (true) {
        const row = rows[rowIndex];
        if (!row) {
          // First activity in this row
          rows[rowIndex] = { end };
          break;
        }

        // If this activity starts after or exactly when the last one on this row ends,
        // they do not overlap and can share the same row.
        if (start >= row.end) {
          row.end = end;
          break;
        }

        // Otherwise try the next row
        rowIndex++;
      }

      if (activity.id === currentActivity.id) {
        rowForCurrent = rowIndex;
        break;
      }
    }

    return rowForCurrent;
  }

  calculateMonthSeparators(): void {
    this.monthSeparatorPositions = [];
    let cumulativeWeeks = 0;

    for (let i = 0; i < this.monthGroups.length - 1; i++) {
      cumulativeWeeks += this.monthGroups[i].weeks.length;
      // Calculate percentage position
      const percentPosition = (cumulativeWeeks / this.weekHeaders.length) * 100;
      this.monthSeparatorPositions.push(percentPosition);
    }
  }

  getMaxOverlapDepth(activities: Activity[]): number {
    let maxDepth = 1;

    for (const activity of activities) {
      const depth = this.calculateRowOffset(activity, activities) + 1;
      maxDepth = Math.max(maxDepth, depth);
    }

    return maxDepth;
  }

  getRowHeight(project: Project): string {
    const maxDepth = this.getMaxOverlapDepth(project.activities);
    const baseHeight = 52;
    const heightPerRow = 44;
    const totalHeight = baseHeight + ((maxDepth - 1) * heightPerRow);
    return totalHeight + 'px';
  }

  getFormatDate(date: Date): string {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  getActivityDateRange(activity: Activity): string {
    const startDate = this.getFormatDate(activity.startDate);
    const endDate = this.getFormatDate(activity.endDate);
    return `${startDate} - ${endDate}`;
  }

  getActivityTooltip(activity: Activity, project: Project): string {
    // return "abcd";s
    const dateRange = this.getActivityDateRange(activity);
    const duration = this.getActivityDuration(activity);
    return `Task: ${activity.name}\nProject: ${project.name}\nDates: ${dateRange}\nDuration: ${duration}`;
  }

  getStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'planned': 'Planned',
      'in-progress': 'In Progress',
      'completed': 'Completed',
      'at-risk': 'At Risk'
    };
    return labels[status] || status;
  }

  toggleProjectFilter(projectId: string): void {
    if (this.selectedProjectIds.has(projectId)) {
      this.selectedProjectIds.delete(projectId);
    } else {
      this.selectedProjectIds.add(projectId);
    }
  }

  isProjectSelected(projectId: string): boolean {
    return this.selectedProjectIds.has(projectId);
  }

  isProjectVisible(projectId: string): boolean {
    return this.selectedProjectIds.has(projectId);
  }

  // Select All / Deselect All helpers for project filters
  areAllProjectsSelected(): boolean {
    if (!this.projects || this.projects.length === 0) {
      return false;
    }
    return this.projects.every(project => this.selectedProjectIds.has(project.id));
  }

  selectAllProjects(): void {
    this.projects.forEach(project => this.selectedProjectIds.add(project.id));
  }

  deselectAllProjects(): void {
    this.selectedProjectIds.clear();
  }

  // Toggle form expansion states
  toggleProjectForm(): void {
    this.isProjectFormCollapsed = !this.isProjectFormCollapsed;
  }

  toggleTaskForm(): void {
    this.isTaskFormCollapsed = !this.isTaskFormCollapsed;
  }

  // Add New Project Methods
  canAddProject(): boolean {
    return this.newProject.name.trim() !== '' &&
      this.newProject.color !== '';
  }

  // Check if color already exists
  isColorDuplicate(color: string): boolean {
    return this.projects.some(p => p.color.toLowerCase() === color.toLowerCase());
  }

  addNewProject(): void {
    if (!this.canAddProject()) {
      this.notificationService.showError('Please fill in all required fields');
      return;
    }

    // Check for duplicate color code
    if (this.isColorDuplicate(this.newProject.color)) {
      this.colorErrorMessage = 'This color is already in use. Please select a different color.';
      this.notificationService.showError('Error: Color code already exists! Please select a different color.');
      return;
    }

    this.colorErrorMessage = '';

    // Create unique ID for project
    const newProjectId = `proj${Date.now()}`;

    // Generate a unique code based on timestamp
    const generatedCode = `PRJ${Date.now().toString().slice(-6)}`;

    // Create new project
    const project: Project = {
      id: newProjectId,
      name: this.newProject.name.trim(),
      code: generatedCode,
      color: this.newProject.color,
      activities: []
    };

    // Add project to list
    this.projects.push(project);

    // Auto-select the new project for visibility
    this.selectedProjectIds.add(newProjectId);

    // Reset form
    this.newProject = {
      name: '',
      color: '#3b82f6'
    };

    this.notificationService.showSuccess('Project added successfully!');

    // Close the dialog
    if (this.projectDialogRef) {
      this.projectDialogRef.close();
    }
  }

  // Add New Task Methods
  canAddTask(): boolean {
    return this.newTask.projectId !== '' &&
      this.newTask.name.trim() !== '' &&
      this.newTask.startDate !== null &&
      this.newTask.endDate !== null;
  }

  addNewTask(): void {
    if (!this.canAddTask()) {
      this.notificationService.showError('Please fill in all required fields');
      return;
    }

    // Find the project
    const project = this.projects.find(p => p.id === this.newTask.projectId);
    if (!project) {
      this.notificationService.showError('Project not found');
      return;
    }

    // Validate dates
    if (!this.newTask.startDate || !this.newTask.endDate || this.newTask.startDate >= this.newTask.endDate) {
      this.notificationService.showError('Start date must be before end date');
      return;
    }

    const createPayload = {
      projectId: String(project.id),
      projectName: project.name,
      colorCode: project.code,
      colorName: project.color,
      taskDescription: this.newTask.name?.trim(),
      startDate: this.getFormatDate(this.newTask.startDate),
      endDate: this.getFormatDate(this.newTask.endDate),
      createdBy: this.userName
    };

    const updatePayload = {
      taskId: String(this.editingTaskId),
      updatedBy: this.userName,
      taskDescription: this.newTask.name?.trim(),
      startDate: this.getFormatDate(this.newTask.startDate),
      endDate: this.getFormatDate(this.newTask.endDate)
    };

    const onComplete = (message: string) => {
      this.notificationService.showSuccess(message);
      this.initializeProjects();
      this.activeProjectsHandler();
      this.isEditingTask = false;
      this.editingTaskId = null;
      this.newTask = {
        projectId: '',
        name: '',
        startDate: null,
        endDate: null
      };
      if (this.taskDialogRef) {
        this.taskDialogRef.close();
      }
    };

    if (this.isEditingTask && this.editingTaskId) {
      this.reportService.updateActivityHandler(updatePayload, this.editingTaskId)
        .subscribe(res => {
          console.info('Task updated successfully', res);
          onComplete('Task updated successfully!');
        });
    } else {
      this.reportService.createActivityHandler(createPayload)
        .subscribe(res => {
          console.info('Task added successfully', res);
          onComplete('Task added successfully!');
        });
    }
  }

  private getActivityDateTime(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    // Fixed time component to match required format
    return `${year}-${month}-${day} 13:30:00.000`;
  }
}

